import { useState, useEffect, useCallback } from 'react';

export interface TranslationEntry {
  id: string;
  sourceText: string;
  translatedText: string;
  sourceLanguage: string;
  targetLanguage: string;
  timestamp: Date;
  isFavorite: boolean;
  tags?: string[];
  category?: string;
}

const STORAGE_KEY = 'translateloo-history';
const MAX_HISTORY_ENTRIES = 1000; // Prevent unlimited growth

class TranslationHistoryService {
  private static instance: TranslationHistoryService;

  static getInstance(): TranslationHistoryService {
    if (!TranslationHistoryService.instance) {
      TranslationHistoryService.instance = new TranslationHistoryService();
    }
    return TranslationHistoryService.instance;
  }

  loadHistory(): TranslationEntry[] {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      return parsed.map((entry: any) => ({
        ...entry,
        timestamp: new Date(entry.timestamp)
      }));
    } catch (error) {
      console.error('Failed to load translation history:', error);
      return [];
    }
  }

  saveHistory(entries: TranslationEntry[]): void {
    try {
      // Keep only the most recent entries to prevent storage bloat
      const trimmedEntries = entries.slice(0, MAX_HISTORY_ENTRIES);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedEntries));
    } catch (error) {
      console.error('Failed to save translation history:', error);
    }
  }

  addEntry(entry: Omit<TranslationEntry, 'id' | 'timestamp'>): TranslationEntry {
    const newEntry: TranslationEntry = {
      ...entry,
      id: crypto.randomUUID(),
      timestamp: new Date(),
      isFavorite: false,
      tags: [],
      category: undefined
    };

    const currentHistory = this.loadHistory();

    // Check for duplicates (same source text, languages, and result)
    const isDuplicate = currentHistory.some(existing =>
      existing.sourceText === newEntry.sourceText &&
      existing.sourceLanguage === newEntry.sourceLanguage &&
      existing.targetLanguage === newEntry.targetLanguage &&
      existing.translatedText === newEntry.translatedText
    );

    if (!isDuplicate) {
      const updatedHistory = [newEntry, ...currentHistory];
      this.saveHistory(updatedHistory);
    }

    return newEntry;
  }

  updateEntry(id: string, updates: Partial<TranslationEntry>): void {
    const history = this.loadHistory();
    const updatedHistory = history.map(entry =>
      entry.id === id ? { ...entry, ...updates } : entry
    );
    this.saveHistory(updatedHistory);
  }

  deleteEntry(id: string): void {
    const history = this.loadHistory();
    const filteredHistory = history.filter(entry => entry.id !== id);
    this.saveHistory(filteredHistory);
  }

  deleteMultiple(ids: string[]): void {
    const history = this.loadHistory();
    const filteredHistory = history.filter(entry => !ids.includes(entry.id));
    this.saveHistory(filteredHistory);
  }

  toggleFavorite(id: string): void {
    const history = this.loadHistory();
    const updatedHistory = history.map(entry =>
      entry.id === id ? { ...entry, isFavorite: !entry.isFavorite } : entry
    );
    this.saveHistory(updatedHistory);
  }

  searchHistory(query: string, filters?: {
    sourceLanguage?: string;
    targetLanguage?: string;
    category?: string;
    tags?: string[];
    onlyFavorites?: boolean;
    dateFrom?: Date;
    dateTo?: Date;
  }): TranslationEntry[] {
    const history = this.loadHistory();

    return history.filter(entry => {
      // Text search
      if (query) {
        const searchLower = query.toLowerCase();
        const matchesText =
          entry.sourceText.toLowerCase().includes(searchLower) ||
          entry.translatedText.toLowerCase().includes(searchLower);
        if (!matchesText) return false;
      }

      // Filters
      if (filters) {
        if (filters.sourceLanguage && entry.sourceLanguage !== filters.sourceLanguage) return false;
        if (filters.targetLanguage && entry.targetLanguage !== filters.targetLanguage) return false;
        if (filters.category && entry.category !== filters.category) return false;
        if (filters.onlyFavorites && !entry.isFavorite) return false;
        if (filters.tags && filters.tags.length > 0) {
          const hasAllTags = filters.tags.every(tag => entry.tags?.includes(tag));
          if (!hasAllTags) return false;
        }

        // Date range filtering with proper day boundary normalization
        if (filters.dateFrom) {
          // Normalize dateFrom to start of day (00:00:00.000)
          const fromStart = new Date(filters.dateFrom);
          fromStart.setHours(0, 0, 0, 0);
          if (entry.timestamp < fromStart) return false;
        }
        if (filters.dateTo) {
          // Normalize dateTo to end of day (23:59:59.999)
          const toEnd = new Date(filters.dateTo);
          toEnd.setHours(23, 59, 59, 999);
          if (entry.timestamp > toEnd) return false;
        }
      }

      return true;
    });
  }

  exportHistory(format: 'json' | 'csv' = 'json'): string {
    const history = this.loadHistory();

    if (format === 'csv') {
      const headers = ['Source Text', 'Translated Text', 'Source Language', 'Target Language', 'Date', 'Favorite', 'Category', 'Tags'];
      const rows = history.map(entry => [
        `\"${entry.sourceText.replace(/\"/g, '\"\"')}\"`,
        `\"${entry.translatedText.replace(/\"/g, '\"\"')}\"`,
        entry.sourceLanguage,
        entry.targetLanguage,
        entry.timestamp.toISOString(),
        entry.isFavorite ? 'Yes' : 'No',
        entry.category || '',
        entry.tags?.join('; ') || ''
      ]);

      return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    }

    return JSON.stringify(history, null, 2);
  }

  importHistory(data: string, format: 'json' | 'csv' = 'json'): { success: boolean; imported: number; errors: string[] } {
    const errors: string[] = [];
    let imported = 0;

    try {
      let entries: any[] = [];

      if (format === 'json') {
        entries = JSON.parse(data);
      } else {
        // CSV parsing (basic implementation)
        const lines = data.split('\n');
        const headers = lines[0].split(',');
        entries = lines.slice(1).map(line => {
          const values = line.split(',');
          return {
            sourceText: values[0]?.replace(/^\"|\"$/g, '').replace(/\"\"/g, '\"'),
            translatedText: values[1]?.replace(/^\"|\"$/g, '').replace(/\"\"/g, '\"'),
            sourceLanguage: values[2],
            targetLanguage: values[3],
            timestamp: values[4],
            isFavorite: values[5] === 'Yes',
            category: values[6] || undefined,
            tags: values[7] ? values[7].split('; ') : []
          };
        });
      }

      const currentHistory = this.loadHistory();
      const validEntries: TranslationEntry[] = [];

      entries.forEach((entry, index) => {
        try {
          const validEntry: TranslationEntry = {
            id: entry.id || crypto.randomUUID(),
            sourceText: entry.sourceText || '',
            translatedText: entry.translatedText || '',
            sourceLanguage: entry.sourceLanguage || 'auto',
            targetLanguage: entry.targetLanguage || 'en',
            timestamp: new Date(entry.timestamp || Date.now()),
            isFavorite: Boolean(entry.isFavorite),
            tags: Array.isArray(entry.tags) ? entry.tags : [],
            category: entry.category || undefined
          };

          if (validEntry.sourceText && validEntry.translatedText) {
            validEntries.push(validEntry);
            imported++;
          } else {
            errors.push(`Row ${index + 1}: Missing required text fields`);
          }
        } catch (error) {
          errors.push(`Row ${index + 1}: ${error instanceof Error ? error.message : 'Invalid format'}`);
        }
      });

      // Merge with existing history (avoiding duplicates)
      const mergedHistory = [...validEntries, ...currentHistory];
      const uniqueHistory = mergedHistory.filter((entry, index, self) =>
        index === self.findIndex(e =>
          e.sourceText === entry.sourceText &&
          e.translatedText === entry.translatedText &&
          e.sourceLanguage === entry.sourceLanguage &&
          e.targetLanguage === entry.targetLanguage
        )
      );

      this.saveHistory(uniqueHistory);

      return { success: true, imported, errors };
    } catch (error) {
      return {
        success: false,
        imported: 0,
        errors: [`Failed to parse ${format.toUpperCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`]
      };
    }
  }

  clearHistory(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export const useTranslationHistory = () => {
  const [entries, setEntries] = useState<TranslationEntry[]>([]);
  const service = TranslationHistoryService.getInstance();

  // Load history on mount
  useEffect(() => {
    setEntries(service.loadHistory());
  }, []);

  const addEntry = useCallback((entry: Omit<TranslationEntry, 'id' | 'timestamp'>) => {
    const newEntry = service.addEntry(entry);
    setEntries(service.loadHistory()); // Refresh from storage
    return newEntry;
  }, [service]);

  const updateEntry = useCallback((id: string, updates: Partial<TranslationEntry>) => {
    service.updateEntry(id, updates);
    setEntries(service.loadHistory());
  }, [service]);

  const deleteEntry = useCallback((id: string) => {
    service.deleteEntry(id);
    setEntries(service.loadHistory());
  }, [service]);

  const deleteMultiple = useCallback((ids: string[]) => {
    service.deleteMultiple(ids);
    setEntries(service.loadHistory());
  }, [service]);

  const toggleFavorite = useCallback((id: string) => {
    service.toggleFavorite(id);
    setEntries(service.loadHistory());
  }, [service]);

  const searchHistory = useCallback((query: string, filters?: Parameters<typeof service.searchHistory>[1]) => {
    return service.searchHistory(query, filters);
  }, [service]);

  const exportHistory = useCallback((format: 'json' | 'csv' = 'json') => {
    return service.exportHistory(format);
  }, [service]);

  const importHistory = useCallback((data: string, format: 'json' | 'csv' = 'json') => {
    const result = service.importHistory(data, format);
    if (result.success) {
      setEntries(service.loadHistory()); // Refresh after import
    }
    return result;
  }, [service]);

  const clearHistory = useCallback(() => {
    service.clearHistory();
    setEntries([]);
  }, [service]);

  return {
    entries,
    addEntry,
    updateEntry,
    deleteEntry,
    deleteMultiple,
    toggleFavorite,
    searchHistory,
    exportHistory,
    importHistory,
    clearHistory
  };
};
